import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, BookOpen, TerminalSquare, LogOut, Sun, Moon, Server, Award, ShieldCheck, GraduationCap, MonitorCog, Users, KeyboardOff, Trophy, Cloud, BadgeCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true, id: "nav-dashboard" },
  { to: "/curriculum", label: "Curriculum", icon: BookOpen, id: "nav-curriculum" },
  { to: "/quizzes", label: "Quizzes", icon: GraduationCap, id: "nav-quizzes" },
  { to: "/commands", label: "Commands", icon: TerminalSquare, id: "nav-commands" },
  { to: "/certificates", label: "Achievements", icon: Award, id: "nav-certificates" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  if (user?.must_change_password) return <Navigate to="/change-password" replace />;

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-64 shrink-0 border-r border-border flex flex-col fixed h-screen bg-card">
        <div className="px-6 py-6 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary grid place-items-center text-primary-foreground">
            <Server className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-extrabold text-base leading-none tracking-tight">HMG Linux Lab</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Live Lab Simulator</div>
          </div>
        </div>

        <button
          onClick={() => navigate("/labs")}
          data-testid="nav-back-to-labs"
          className="mx-3 mb-1 flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <ArrowLeft className="h-[18px] w-[18px]" /> Back to HMG Labs
        </button>

        <nav className="px-3 mt-2 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              {n.label}
            </NavLink>
          ))}
          {(user?.role === "instructor" || user?.role === "admin") && (
            <NavLink
              to="/instructor"
              data-testid="nav-instructor"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <Users className="h-[18px] w-[18px]" />
              Instructor Portal
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <ShieldCheck className="h-[18px] w-[18px]" />
              Admin Portal
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/admin/anti-paste"
              data-testid="nav-antipaste"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <KeyboardOff className="h-[18px] w-[18px]" />
              Manual Entry
            </NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/admin/leaderboard"
              data-testid="nav-admin-leaderboard"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`
              }
            >
              <Trophy className="h-[18px] w-[18px]" />
              Leaderboard Settings
            </NavLink>
          )}
        </nav>

        <div className="mt-auto px-3 pb-4 space-y-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggle}
            data-testid="theme-toggle"
            className="w-full justify-start gap-3 text-muted-foreground"
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            {theme === "dark" ? "Light mode" : "Dark mode"}
          </Button>
          <div className="px-3 py-3 rounded-md border border-border">
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            <span className="inline-block mt-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-secondary text-secondary-foreground">
              {user?.role}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-3"
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
